/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Anokh J Ajai
 */

import {
  createDataAttribute as sanityCreateDataAttribute,
  CreateDataAttribute as CreateDataAttributeType,
  CreateDataAttributeProps as CreateDataAttributePropsType,
  VisualEditing,
} from "next-sanity";

export type CreateDataAttributeProps = CreateDataAttributePropsType;

export type CreateDataAttribute<T extends CreateDataAttributeProps> = CreateDataAttributeType<T>;

export function createDataAttribute<T extends CreateDataAttributeProps>(
  props: T,
): CreateDataAttribute<T> | undefined {
  if (props?.id && props?.type) {
    return sanityCreateDataAttribute<T>(props);
  }
  return undefined;
}

export function isInSanityIframe(): boolean {
  console.log("Checking if in Sanity iframe...");

  if (typeof window !== "undefined") {
    const isInIframe = window.self !== window.top;
    const isSanityStudio = document.referrer.includes("sanity.studio");

    console.log("Window is defined");
    console.log("isInIframe:", isInIframe);
    console.log("isSanityStudio:", isSanityStudio);

    if (isInIframe && isSanityStudio) {
      console.log("✅ isInSanityIframe conditions met");
      return true;
    } else {
      console.log("❌ isInSanityIframe conditions not met");
    }
  }

  return false;
}

export function isInPreviewMode(): boolean {
  let isInPreview = false;

  console.log("🚫 Disabled **preview mode** to un-break SSG");
  console.log("🔍 Checking if in **preview mode**...");

  // Server-side check (Only call cookies() if it's safe)
  if (typeof window === "undefined") {
    console.log("🖥️ Running on **server side**");

    try {
      const { cookies } = require("next/headers"); // Import dynamically only on the server
      console.log({ cookies });
      const draftModeCookie = cookies().get("__prerender_bypass"); // Next.js uses this cookie for draft mode

      console.log("🍪 Draft mode cookie:", draftModeCookie);
      isInPreview = Boolean(draftModeCookie);
      console.log("✅ isInPreview (server):", isInPreview);
    } catch (error) {
      console.error("❌ Cookies API is unavailable or failed on the server:", error);
    }
  }
  // Client-side check (Sanity Studio iframe or URL)
  else {
    console.log("🌐 Running on **client side**");
    isInPreview = isInSanityIframe();
    console.log("🧪 isInPreview (client):", isInPreview);
  }

  return isInPreview;
}

export { VisualEditing };
